import React from 'react';
import { FiClock, FiCheckCircle, FiPackage, FiTruck, FiHome, FiXCircle } from 'react-icons/fi';

const steps = [
  { key: 'pending', label: 'Order Placed', icon: FiClock },
  { key: 'confirmed', label: 'Confirmed', icon: FiCheckCircle },
  { key: 'processing', label: 'Processing', icon: FiPackage },
  { key: 'shipped', label: 'Shipped', icon: FiTruck },
  { key: 'delivered', label: 'Delivered', icon: FiHome },
];

const statusIndex = {
  pending: 0,
  confirmed: 1,
  processing: 2,
  shipped: 3,
  delivered: 4,
  cancelled: -1,
  refunded: -1,
};

const OrderTimeline = ({ order }) => {
  const currentStatus = order.status;
  const currentIdx = statusIndex[currentStatus] ?? -1;
  const isCancelled = currentStatus === 'cancelled' || currentStatus === 'refunded';

  if (isCancelled) {
    return (
      <div className="d-flex align-items-center gap-3 p-3 rounded" style={{ background: '#FEF2F2' }}>
        <FiXCircle size={28} className="text-danger" />
        <div>
          <strong className="text-danger text-capitalize">{currentStatus}</strong>
          <p className="text-muted mb-0 small">
            This order has been {currentStatus}.
          </p>
        </div>
      </div>
    );
  }

  const getTimestamp = (step) => {
    switch (step) {
      case 'pending': return order.createdAt;
      case 'confirmed': return order.confirmedAt;
      case 'shipped': return order.shippedAt;
      case 'delivered': return order.deliveredAt;
      default: return null;
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between position-relative" style={{ padding: '0 20px' }}>
        {/* Connecting line */}
        <div
          className="position-absolute"
          style={{
            top: '20px',
            left: '40px',
            right: '40px',
            height: '3px',
            background: '#E5E7EB',
            zIndex: 0,
          }}
        />
        {/* Progress line */}
        <div
          className="position-absolute"
          style={{
            top: '20px',
            left: '40px',
            width: currentIdx >= 0 ? `${(currentIdx / (steps.length - 1)) * (100 - 16)}%` : '0%',
            height: '3px',
            background: '#B91C1C',
            zIndex: 1,
            transition: 'width 0.5s ease',
          }}
        />

        {steps.map((step, idx) => {
          const isCompleted = idx <= currentIdx;
          const isCurrent = idx === currentIdx;
          const Icon = step.icon;
          const timestamp = getTimestamp(step.key);

          return (
            <div
              key={step.key}
              className="d-flex flex-column align-items-center"
              style={{ zIndex: 2, flex: 1 }}
            >
              <div
                className="d-flex align-items-center justify-content-center rounded-circle mb-2"
                style={{
                  width: '40px',
                  height: '40px',
                  background: isCompleted ? '#B91C1C' : '#fff',
                  border: `3px solid ${isCompleted ? '#B91C1C' : '#E5E7EB'}`,
                  transition: 'all 0.3s ease',
                  boxShadow: isCurrent ? '0 0 0 4px rgba(185, 28, 28, 0.2)' : 'none',
                }}
              >
                <Icon
                  size={18}
                  style={{ color: isCompleted ? '#fff' : '#9CA3AF' }}
                />
              </div>
              <span
                className="small fw-semibold text-center"
                style={{
                  color: isCompleted ? '#B91C1C' : '#9CA3AF',
                  fontSize: '0.75rem',
                }}
              >
                {step.label}
              </span>
              {timestamp && (
                <span className="text-muted text-center" style={{ fontSize: '0.65rem' }}>
                  {new Date(timestamp).toLocaleDateString('en-IN', {
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* Tracking info */}
      {order.trackingNumber && (
        <div className="mt-3 p-3 rounded d-flex align-items-center gap-3" style={{ background: '#FEF3C7' }}>
          <FiTruck size={20} style={{ color: '#92400E' }} />
          <div>
            {order.shippingCarrier && (
              <span className="small fw-semibold" style={{ color: '#92400E' }}>
                {order.shippingCarrier} ·{' '}
              </span>
            )}
            <span className="small" style={{ color: '#92400E' }}>
              Tracking: <strong>{order.trackingNumber}</strong>
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderTimeline;
